import type {ComponentProps, Key, Ref} from "react";

import {useGetSession} from "@project/application";
import {
  Avatar,
  Button,
  Dropdown,
  Label,
  ListBox,
  Sheet,
  Typography,
  getAvatarImageUrl,
  useSidebar,
} from "@project/design-system-web";
import {useI18n} from "@project/i18n/react";
import {ChevronDownIcon, ChevronUpIcon, GearIcon, LogOutIcon} from "@project/icons-web";
import {routes} from "@project/router";
import {useNavigate} from "@tanstack/react-router";
import {useEffect, useRef, useState} from "react";

import {appShellMessages} from "../../../messages/app-shell.messages";
import {useShellConfirm} from "../shell-confirm-provider";

import {useLogoutForm} from "./use-logout-form";
import {getUserEmailLabel, navigateToAccountConsole} from "./user-menu.utils";

interface UserMenuProps {
  presentation: "desktop" | "mobile";
}

interface UserMenuTriggerProps extends Omit<ComponentProps<typeof Button>, "children"> {
  avatarSrc: string;
  chevron: "up" | "down";
  email?: string;
  name: string;
  ref?: Ref<HTMLButtonElement>;
}

function getUserInitials(name: string) {
  const initials = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return initials || "?";
}

export function UserMenuTrigger({
  avatarSrc,
  chevron,
  email,
  name,
  ref,
  ...buttonProps
}: UserMenuTriggerProps) {
  const ChevronIcon = chevron === "up" ? ChevronUpIcon : ChevronDownIcon;
  const emailLabel = getUserEmailLabel(email);

  return (
    <Button
      ref={ref}
      className="h-auto w-full min-w-0 justify-start gap-2 rounded-2xl px-2 py-2 [.sidebar[data-state=collapsed]_&]:justify-center [.sidebar[data-state=collapsed]_&]:px-0"
      variant="ghost"
      {...buttonProps}
    >
      <Avatar className="shrink-0" size="sm" variant="soft">
        <Avatar.Image alt={name} src={avatarSrc} />
        <Avatar.Fallback>
          <span className="max-w-full truncate text-xs">{getUserInitials(name)}</span>
        </Avatar.Fallback>
      </Avatar>
      <div className="flex min-w-0 flex-1 flex-col items-start gap-0 [.sidebar[data-state=collapsed]_&]:hidden">
        <Typography className="text-foreground max-w-full truncate text-sm leading-5 font-medium">
          {name}
        </Typography>
        {emailLabel && (
          <Typography className="text-muted max-w-full truncate text-xs leading-none">
            {emailLabel}
          </Typography>
        )}
      </div>
      <ChevronIcon className="text-muted ml-auto size-3.5 shrink-0 [.sidebar[data-state=collapsed]_&]:hidden" />
    </Button>
  );
}

/**
 * UserMenu — account actions anchored to current user's avatar.
 *
 * Desktop uses a dropdown menu. Mobile uses a nested action sheet with a ListBox
 * so account actions scale to future profile options without cramped touch UI.
 */
export function UserMenu({presentation}: UserMenuProps) {
  const {t} = useI18n();
  const navigate = useNavigate();
  const confirm = useShellConfirm();
  const {setMobileOpen} = useSidebar();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const justOpenedViaTrigger = useRef(false);
  const {data: session} = useGetSession();
  const {logoutForm, submitLogout} = useLogoutForm();
  const currentUser = session?.user;

  /*
   * Custom outside-click dismiss for the desktop dropdown.
   *
   * React Aria's useInteractOutside ignores clicks on elements matching
   * [data-react-aria-top-layer] (header, sidebar). This listener bypasses
   * that filter so the dropdown closes on any outside click.
   */
  useEffect(() => {
    if (!isDropdownOpen || presentation !== "desktop") return;

    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node | null;

      if (!target) return;

      const clickedTrigger = triggerRef.current?.contains(target);
      const clickedPopover = popoverRef.current?.contains(target);

      if (!clickedTrigger && !clickedPopover) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("pointerdown", onPointerDown, true);

    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [isDropdownOpen, presentation]);

  if (!currentUser) return null;

  const avatarSrc = getAvatarImageUrl("default.png");

  function handleDropdownOpenChange(open: boolean) {
    if (open) justOpenedViaTrigger.current = true;
    setIsDropdownOpen(open);
  }

  function closeMobileSidebar() {
    if (presentation === "mobile") setMobileOpen(false);
  }

  function confirmLogout() {
    requestAnimationFrame(() => {
      void confirm({
        title: t(appShellMessages.logOutTitle),
        description: t(appShellMessages.logOutDescription),
        confirmLabel: t(appShellMessages.logOut),
        confirmVariant: "danger",
        status: "warning",
      }).then((confirmed) => {
        if (confirmed) submitLogout();
      });
    });
  }

  function handleAction(key: Key) {
    setIsDropdownOpen(false);
    setIsMobileSheetOpen(false);

    if (key === "settings") {
      closeMobileSidebar();
      void navigate({to: routes.settings()});
    }

    if (key === "account-security") {
      closeMobileSidebar();
      navigateToAccountConsole(window.location.assign.bind(window.location));
    }

    if (key === "logout") {
      confirmLogout();
    }
  }

  if (presentation === "mobile") {
    return (
      <>
        <Sheet.NestedRoot isOpen={isMobileSheetOpen} onOpenChange={setIsMobileSheetOpen}>
          <Sheet.Trigger>
            <UserMenuTrigger
              avatarSrc={avatarSrc}
              chevron="down"
              email={currentUser.email}
              name={currentUser.displayName}
            />
          </Sheet.Trigger>
          <Sheet.Backdrop>
            <Sheet.Content className="mx-auto max-h-[95vh] max-w-[420px]">
              <Sheet.Dialog>
                <Sheet.Handle />
                <Sheet.CloseTrigger />
                <Sheet.Header>
                  <div className="flex min-w-0 items-center gap-3 pr-8">
                    <Avatar className="shrink-0" size="md" variant="soft">
                      <Avatar.Image alt={currentUser.displayName} src={avatarSrc} />
                      <Avatar.Fallback>
                        <span className="max-w-full truncate text-sm">
                          {getUserInitials(currentUser.displayName)}
                        </span>
                      </Avatar.Fallback>
                    </Avatar>
                    <div className="flex min-w-0 flex-1 flex-col gap-0">
                      <Sheet.Heading className="truncate text-base">
                        {currentUser.displayName}
                      </Sheet.Heading>
                      {getUserEmailLabel(currentUser.email) && (
                        <Typography className="text-muted truncate text-sm leading-5">
                          {getUserEmailLabel(currentUser.email)}
                        </Typography>
                      )}
                    </div>
                  </div>
                </Sheet.Header>
                <Sheet.Body className="min-h-0 px-2 pt-0 pb-3">
                  <ListBox
                    aria-label={t(appShellMessages.accountActions)}
                    className="w-full"
                    selectionMode="none"
                    onAction={handleAction}
                  >
                    <ListBox.Item id="settings" textValue={t(appShellMessages.settings)}>
                      <GearIcon className="text-muted size-4 shrink-0" />
                      <Label>{t(appShellMessages.settings)}</Label>
                    </ListBox.Item>
                    <ListBox.Item
                      id="account-security"
                      textValue={t(appShellMessages.manageSignInAndSecurity)}
                    >
                      <GearIcon className="text-muted size-4 shrink-0" />
                      <Label>{t(appShellMessages.manageSignInAndSecurity)}</Label>
                    </ListBox.Item>
                    <ListBox.Item
                      id="logout"
                      textValue={t(appShellMessages.logOut)}
                      variant="danger"
                    >
                      <LogOutIcon className="text-danger size-4 shrink-0" />
                      <Label>{t(appShellMessages.logOut)}</Label>
                    </ListBox.Item>
                  </ListBox>
                </Sheet.Body>
              </Sheet.Dialog>
            </Sheet.Content>
          </Sheet.Backdrop>
        </Sheet.NestedRoot>

        {logoutForm}
      </>
    );
  }

  return (
    <>
      <Dropdown isOpen={isDropdownOpen} onOpenChange={handleDropdownOpenChange}>
        <Dropdown.Trigger aria-label={t(appShellMessages.userMenu)}>
          <UserMenuTrigger
            ref={triggerRef}
            avatarSrc={avatarSrc}
            chevron="up"
            email={currentUser.email}
            name={currentUser.displayName}
            onClick={() => {
              if (justOpenedViaTrigger.current) {
                justOpenedViaTrigger.current = false;

                return;
              }
              if (isDropdownOpen) setIsDropdownOpen(false);
            }}
          />
        </Dropdown.Trigger>

        <Dropdown.Popover placement="top start">
          <div ref={popoverRef}>
            <Dropdown.Menu onAction={handleAction}>
              <Dropdown.Item id="settings" textValue={t(appShellMessages.settings)}>
                <div className="flex w-full items-center justify-between gap-2">
                  <span>{t(appShellMessages.settings)}</span>
                  <GearIcon className="text-muted size-3.5" />
                </div>
              </Dropdown.Item>
              <Dropdown.Item
                id="account-security"
                textValue={t(appShellMessages.manageSignInAndSecurity)}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <span>{t(appShellMessages.manageSignInAndSecurity)}</span>
                  <GearIcon className="text-muted size-3.5" />
                </div>
              </Dropdown.Item>
              <Dropdown.Item id="logout" textValue={t(appShellMessages.logOut)} variant="danger">
                <div className="flex w-full items-center justify-between gap-2">
                  <Label>{t(appShellMessages.logOut)}</Label>
                  <LogOutIcon className="text-danger size-3.5" />
                </div>
              </Dropdown.Item>
            </Dropdown.Menu>
          </div>
        </Dropdown.Popover>
      </Dropdown>

      {logoutForm}
    </>
  );
}
